import { TicketInfo } from '@definitions/ticket'
import { PaintVerdict } from '@enums/paint-verdict'

/**
 * The one threshold set every painted check uses, in ms after the callback: a screen showing the delivered
 * value by the first check is `paint1s`, by the second `paint3s`, by the last `paintslow`; still showing
 * something else at the last check is `gpstuck`.
 */
export const PAINT_CHECKS_MS: readonly number[] = [1000, 3000, 5000]
const PAINT_1S_MS = 1000
const PAINT_3S_MS = 3000

/** A navigation started from a gateway callback that has not reached its URL by then is reported. */
export const NAVIGATION_STUCK_MS = 3000

// One guest tab sends at most this many routine lines per window; warnings are always sent.
export const BURST_CAP_LINES = 5
export const BURST_WINDOW_MS = 60_000

export const NO_TICKET_TAG = 'no-ticket'
const MAX_VALUE_LENGTH = 40
const NONE = 'none'

// Control, format (bidi overrides, zero-width) and line/paragraph separators could fake a new line or reorder
// what a line displays in the Bugfender dashboard.
const INVISIBLE_CHARACTERS = /[\p{C}\p{Zl}\p{Zp}]/gu

/**
 * Whether the screen showed what the callback delivered, and how fast. `shown` is null when the watched
 * element is gone (the dialog closed, the guest navigated away): the guest is past that screen, so it counts
 * as painted. A screen not painted while the tab was hidden (`visible` false: the phone locked right after
 * requesting the car) is `gphidden`, not `gpstuck`: a hidden tab's timers and frames are throttled.
 */
export const paintVerdict = (expected: string, shown: string | null, ms: number, visible: boolean): PaintVerdict => {
	if (shown !== null && shown !== expected) return visible ? PaintVerdict.Stuck : PaintVerdict.Hidden
	if (ms <= PAINT_1S_MS) return PaintVerdict.Paint1s
	if (ms <= PAINT_3S_MS) return PaintVerdict.Paint3s

	return PaintVerdict.PaintSlow
}

/** Whether one more routine line fits, given when the previous ones were sent. */
export const withinBurstCap = (sentAt: readonly number[], now: number): boolean =>
	sentAt.filter(at => now - at < BURST_WINDOW_MS).length < BURST_CAP_LINES

/** A value from outside the app (a server, the DOM) made safe and short enough for one log line. */
export const logValue = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return NONE

	return String(value).replace(INVISIBLE_CHARACTERS, '').slice(0, MAX_VALUE_LENGTH)
}

/** Whether a window has not concluded yet: the screen does not show the expected value. */
export const isUnpainted = (verdict: PaintVerdict): boolean =>
	verdict === PaintVerdict.Stuck || verdict === PaintVerdict.Hidden

export const yesNo = (value: boolean): string => (value ? 'yes' : 'no')

/**
 * The Bugfender tag of every guest-journey line: the ticket's uuid, the same on every visit from the SMS or QR
 * link, so one tag search returns the whole journey of a ticket across visits.
 */
export const ticketLogTag = (ticketUuid: string | null | undefined): string =>
	ticketUuid ? `ticket-${logValue(ticketUuid)}` : NO_TICKET_TAG

/**
 * What the ticket info line keeps: ids, statuses and counts. The payload also carries the guest's name, phone,
 * vehicle description and arrival time, which must never reach the log, so only these fields are read.
 */
export const ticketInfoLogContext = (ticketInfo: TicketInfo): Readonly<Record<string, string | number | boolean>> => ({
	code: logValue(ticketInfo.code),
	operatorCompanyId: ticketInfo.company?.operatorId ?? 0,
	parkingLocationId: ticketInfo.location?.id ?? 0,
	ticketUuid: logValue(ticketInfo.ticket?.ticketUuid),
	requested: ticketInfo.ticket?.requested ?? false,
	payments: ticketInfo.payments?.length ?? 0,
})
